import { useNavigate } from 'react-router';
import { ShieldCheck, Plus } from '@/components/icons';
import { Button, Card, CardBody, EmptyState } from '@/components/ui';
import { timeAgo } from '@/lib/format';
import type { Project, TestCase } from '@/lib/types';

/**
 * The Security tests tab of a security project: its scan configurations (each a "security test"). Opening one
 * shows the project's scope and that scan's history, and runs it. "New scan" creates another configuration.
 */
export function SecurityTestsTab({ project, tests, canWrite }: { project: Project; tests: TestCase[]; canWrite: boolean }) {
  const navigate = useNavigate();
  const scans = tests.filter((t) => t.type === 'SECURITY');
  const newScan = () => navigate(`/projects/${project.id}/security/new`);

  if (scans.length === 0) {
    return (
      <EmptyState
        icon={<ShieldCheck className="size-6" />}
        title="No security scans yet"
        description="Create a scan configuration, confirm the authorized hosts (scope), then run a black-box pentest that grades the app."
        action={canWrite && <Button onClick={newScan} icon={<Plus className="size-4" />}>New scan</Button>}
      />
    );
  }

  return (
    <div className="space-y-4">
      {canWrite && (
        <div className="flex justify-end">
          <Button onClick={newScan} icon={<Plus className="size-4" />}>New scan</Button>
        </div>
      )}
      <Card>
        <CardBody>
          <ul className="divide-y divide-border">
            {scans.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/projects/${project.id}/security/${t.id}`)}
                  className="flex w-full items-center gap-3 px-1 py-3 text-left hover:bg-surface-raised/50"
                >
                  <ShieldCheck className="size-[18px] shrink-0 text-primary" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-fg">{t.name}</span>
                    <span className="block text-xs text-fg-muted">Security scan · created {timeAgo(t.createdAt)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>
    </div>
  );
}
